import { WebDao } from "fe-common/graphql/dao";
import {
  ConversationDriverExchange,
  ConversationInitialResponse,
  ConversationStatus,
  DriverDebugData,
  ExchangeAnswer,
  ExchangeQuestion,
  ExchangeResponse,
} from "./types/driverTypes";
import {
  ConversationExchangeFragment,
  ConversationInfoMessageFragment,
  ConversationNextQuestionFragment,
  ConversationPatientAnswer,
  ConversationQuestionType,
  ConversationStateNextResponseFragment,
  QuestionChoiceType,
  RestoreConversationState,
} from "fe-common/graphql";
import { DriverQuestionsConfig } from "./questions/Question";
import { CONVERSATION_SAVE_TYPE } from "../solverHooks/saveConversation";
import { v4 as uuid } from "uuid";
import { GenderValue } from "shared";
import { MergedConfigurationSettings } from "@kahunmedical/common-configuration";

export type initOptions = {
  patientCaseId?: string;
  sessionId?: string;
  debug?: boolean;
  storageData?: any;
  conversationId?: string;
  clinicId?: string;
};
export const getGenderFromExchanges = (
  exchanges: ConversationDriverExchange[],
) =>
  exchanges.find((e) => e.type === ConversationQuestionType.Gender)?.answer
    ?.qfs?.[0].qualifiers?.[0]?.value?.id as GenderValue;

const toDebug = (
  res,
  question: ConversationNextQuestionFragment,
): DriverDebugData => ({
  ...res,
  nextQuestion: question ? { ...question, progress: res.progress } : null,
  suggestions: res.nextSuggestions || {
    suggestions: [],
    diseases: [],
    clusters: [],
  },
});

const keepAnswered = (e) => e.answer;

export interface ConversationDriverEventListener {
  contextChanged(context: any);
}

export class ConversationDriverImpl {
  private options: initOptions;
  private context: any;
  private currentExchange: ConversationDriverExchange;
  private caseId: string;
  private conversationStatus: ConversationStatus;
  private listeners = new Set<ConversationDriverEventListener>();

  constructor(private dao: WebDao) {}

  private currentExchangeCompleted() {
    return (
      !this.currentExchange?.question?.question &&
      !this.currentExchange?.info?.length
    );
  }

  async init(options: initOptions): Promise<ConversationInitialResponse> {
    const restoreStatus = () => {
      if (this.currentExchangeCompleted()) {
        this.conversationStatus = ConversationStatus.COMPLETED;
      } else {
        this.conversationStatus = ConversationStatus.IN_PROGRESS;
      }
    };

    this.options = options;
    const {
      patientCaseId,
      conversationId,
      storageData,
      clinicId,
    } = this.options;
    this.caseId = patientCaseId;
    this.conversationStatus = ConversationStatus.CREATED;

    let res;
    if (conversationId) {
      //Restore conversation by conversationID - THIS IS FOR BACKWARD COMPATABILITY FOR OLD CONVERSATIONS
      const context = await this.legacyRestoreContext(conversationId);
      res = await this.restoreFromContext({ context });
    } else if (storageData && !patientCaseId) {
      res = await this.restoreFromContext(storageData);
    } else {
      res = await this.dao.startConversation(
        //start conversation, if patientCaseId is provided use it to init.
        patientCaseId,
        clinicId,
        this.options.debug,
      );
    }
    // console.log("driver init", {
    //   conversationId,
    //   res,
    //   storageData,
    //   patientCaseId,
    // });

    this.caseId = res?.caseId;
    this.setContext(res?.context);

    const exchanges = [];
    if (res?.initialMessages?.length) {
      exchanges.push(this.createInfoExchange(res.initialMessages));
    }
    exchanges.push(
      ...this.restoredExchangeToConversationExchange(res?.exchanges),
    );

    if (res?.nextResponse?.messages) {
      const exchange = this.createInfoExchange(res.nextResponse.messages);
      exchanges.push(exchange);
      this.currentExchange = exchange;
      this.setContext(res.context);
    } else {
      this.setNewExchangeAndContext({
        ...res,
        nextQuestion: res.nextResponse.question,
      });
      exchanges.push(this.currentExchange);
    }

    const debug = toDebug(
      res,
      res.exchanges?.[res?.exchanges?.length - 1]?.question,
    );

    restoreStatus();

    return {
      exchanges,
      debug,
      gender: res?.gender || getGenderFromExchanges(exchanges),
    };
  }

  private async legacyRestoreContext(conversationId: string) {
    const result = await this.dao.restoreOpaqueData(
      conversationId,
      CONVERSATION_SAVE_TYPE,
    );
    return result.data.context;
  }

  private exchangeId(): string {
    return uuid();
  }

  public getCaseId() {
    //for read only cases the server creates a new caseId and works on it, so the original case is not changes.
    //For that case we want to return the original id.
    return this.options?.patientCaseId || this.caseId;
  }

  public getCurrentExchange(): ConversationDriverExchange {
    return this.currentExchange;
  }

  /*For code that uses directly the context
   * TODO: Move the calls in to the driver*/
  public getCurrentContext(): any {
    return this.context;
  }

  public getCurrentSettings(): MergedConfigurationSettings {
    return this.dao.getSettings();
  }

  public getConversationStatus() {
    return this.conversationStatus;
  }

  private async restoreFromContext({
    context,
    contextQueue = null,
  }): Promise<RestoreConversationState> {
    const restoreContext = (context, contextQueue) => {
      if (context) {
        this.context = context;
      } else if (contextQueue && Array.isArray(contextQueue)) {
        //for background compatability, we only need the last context
        this.context = contextQueue[0];
      }
    };

    restoreContext(context, contextQueue);
    return await this.dao.restoreConversation(this.context, this.options.debug);
  }

  private createInfoExchange(
    infoMessages: ConversationInfoMessageFragment[],
  ): ConversationDriverExchange {
    return {
      id: this.exchangeId(),
      info: infoMessages,
      type: null,
      question: null,
      hasMore: true,
    };
  }

  private setNewExchangeAndContext({
    nextQuestion,
    progress,
    context,
    urgent,
  }: {
    nextQuestion: ConversationNextQuestionFragment;
    progress: number;
    context: any;
    urgent?: { urgencyLevelId: string };
  }): void {
    this.setContext(context);
    this.currentExchange = {
      type: nextQuestion?.questionType,
      id: this.exchangeId(),
      question: sQuestionToDriverQuestion(nextQuestion, progress),
      urgencyLevel: urgent?.urgencyLevelId,
    };
  }

  public async answer(
    answer: ExchangeAnswer,
    persistMode: boolean = true,
  ): Promise<ExchangeResponse> {
    let { skip, qfs: ansewerQfs } = answer;
    this.currentExchange.answer = answer;
    const type = this.currentExchange.type;
    const { group, source, qfsConstructor } =
      DriverQuestionsConfig[type] ||
      DriverQuestionsConfig[ConversationQuestionType.Suggestion]; // Treat unknown configurations as suggestions

    // TODO: vvv---- Make all groups support qfsConstructor instead of this code ---vvv
    let qfs = { [group]: ansewerQfs };
    // Since chief complains might have more than one qualifier (since june 23)
    // we provided qfsConstructor that can choose which qfs are placed in which group
    if (qfsConstructor) qfs = qfsConstructor(ansewerQfs);

    const params = {
      qfs,
      context: this.context,
      type,
      skipped: skip,
      debug: this.options.debug,
      source,
      saveToCase: !!this.caseId && persistMode,
    };

    let res;
    try {
      res = await this.dao.addConversationFindings(params);
    } catch (e) {
      let er = e as { message: any };
      this.currentExchange.error = er?.message || e;
      return {
        exchange: this.currentExchange,
        debug: null,
      };
    }
    this.setContext(res.context);

    const exchange = this.handleNextResponse(res);

    if (this.currentExchangeCompleted()) {
      this.conversationStatus = ConversationStatus.COMPLETED;
    } else {
      this.conversationStatus = ConversationStatus.IN_PROGRESS;
    }

    return exchange;
  }

  private async handleNextResponse(
    res: ConversationStateNextResponseFragment,
  ): Promise<ExchangeResponse> {
    if (res.nextResponse.messages) {
      if (!this.currentExchange.info) {
        this.currentExchange.info = [];
      }
      this.currentExchange.info.push(...res.nextResponse.messages);
      this.currentExchange.hasMore = true;
      this.setContext(res.context);
    } else {
      this.setNewExchangeAndContext({
        ...res,
        nextQuestion: res.nextResponse.question,
      });
    }

    return {
      exchange: this.currentExchange,
      debug: toDebug(res, res.nextResponse.question),
      gender: res.gender as GenderValue,
    };
  }

  public async getNextResponse(
    persistMode: boolean = true,
  ): Promise<ExchangeResponse> {
    const res = await this.dao.getNextResponse(
      this.context,
      this.options.debug,
      !!this.caseId && persistMode,
    );
    return this.handleNextResponse(res);
  }

  public async retry(): Promise<ExchangeResponse> {
    if (this.currentExchange.error) {
      try {
        // console.log("retry");
        const res = await this.dao.getNextResponse(
          this.context,
          this.options.debug,
        );
        this.currentExchange.error = null;
        return this.handleNextResponse(res);
      } catch (e) {
        return { exchange: this.currentExchange, debug: null };
      }
    }

    return { exchange: this.currentExchange, debug: null };
  }

  public async abandone(immediate: boolean): Promise<ExchangeResponse> {
    //call server
    const res = await this.dao.abandoned(this.context, true);
    this.conversationStatus = ConversationStatus.ABANDONED;
    return !immediate ? this.handleNextResponse(res) : null;
  }

  public async undo(exchange) {
    const res = await this.dao.undoConversation(
      this.context,
      this.options.debug,
    );
    this.setContext(res.context);
    this.currentExchange = exchange;
  }

  private restoredExchangeToConversationExchange(
    restoredExchanges: ConversationExchangeFragment[],
  ): ConversationDriverExchange[] {
    return restoredExchanges
      .map((sExchange) => ({
        id: this.exchangeId(),
        type: sExchange.question.questionType,
        question: sQuestionToDriverQuestion(
          sExchange.question,
          sExchange.progress,
        ),
        answer: sExchange.patientAnswer
          ? {
              qfs: sExchange.answer.map((a) => ({
                ...a.finding,
                feature: { ...a.finding.feature, alias: a.findingAlias },
              })),

              skip:
                sExchange.patientAnswer === ConversationPatientAnswer.Unknown,
            }
          : null,
        info: sExchange.messages,
      }))
      .filter(keepAnswered);
  }

  public async sendFeedback(text: string, name) {
    await this.dao.sendFeedback(text, "", name);
  }

  public async getReasoning(endExchangeIndex?): Promise<DriverDebugData> {
    const res = await this.dao.getReasoning(this.context, endExchangeIndex);
    return toDebug(res, res.nextQuestion);
  }

  private setContext(context: any) {
    if (this.context !== context) {
      this.context = context;
      if (context) this.notifyListeners((l) => l.contextChanged(context));
    }
  }

  public addEventListener(l: ConversationDriverEventListener) {
    this.listeners.add(l);
  }
  public removeEventListener(l: ConversationDriverEventListener) {
    return this.listeners.delete(l);
  }
  private notifyListeners(e: (l: ConversationDriverEventListener) => any) {
    this.listeners.forEach(e);
  }
}

function sQuestionToDriverQuestion(
  sQuestion: ConversationNextQuestionFragment,
  progress: number,
): ExchangeQuestion {
  return {
    question: sQuestion,
    multipleSelection:
      sQuestion?.questionChoiceType === QuestionChoiceType.ChooseMuliple,
    progress,
  };
}
